import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Grid,
  Card,
  CardContent,
  Alert,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Switch,
  FormControlLabel,
  Divider,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Snackbar
} from '@mui/material';
import {
  SmartToy,
  CheckCircle,
  Error as ErrorIcon,
  Info,
  Visibility,
  VisibilityOff,
  ExpandMore,
  Launch,
  ContentCopy,
  AutoAwesome
} from '@mui/icons-material';
import api from '../services/api';

interface CustomGPTChannelData {
  name: string;
  gpt_name: string;
  gpt_description: string;
  greeting_enabled: boolean;
  greeting_message: string;
  auto_assignment_enabled: boolean;
  conversation_retention_days: number;
  rate_limit_per_minute: number;
  rate_limit_per_hour: number;
}

interface CustomGPTChannelResponse {
  id: number;
  channel_id: number;
  name: string;
  gpt_name: string;
  gpt_description: string;
  api_key: string;
  webhook_url: string;
  openapi_schema_url: string;
  privacy_policy_url: string;
  greeting_enabled: boolean;
  greeting_message: string;
  auto_assignment_enabled: boolean;
  conversation_retention_days: number;
  rate_limit_per_minute: number;
  rate_limit_per_hour: number;
  total_requests: number;
  successful_requests: number;
  failed_requests: number;
  last_request_at: string | null;
  enabled: boolean;
  created_at: string;
  updated_at: string;
}

interface CustomGPTChannelConfigProps {
  onChannelCreated: (channel: any) => void;
  editingChannel?: any;
  onCancel: () => void;
}

const CustomGPTChannelConfig: React.FC<CustomGPTChannelConfigProps> = ({
  onChannelCreated,
  editingChannel,
  onCancel
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [copySuccess, setCopySuccess] = useState<string | null>(null);
  const [createdChannel, setCreatedChannel] = useState<CustomGPTChannelResponse | null>(null);

  const [formData, setFormData] = useState<CustomGPTChannelData>({
    name: '',
    gpt_name: '',
    gpt_description: '',
    greeting_enabled: true,
    greeting_message: 'Hello! I\'m connected to our support team. How can I help you?',
    auto_assignment_enabled: true,
    conversation_retention_days: 7,
    rate_limit_per_minute: 60,
    rate_limit_per_hour: 1000
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (editingChannel) {
      loadChannelData();
    }
  }, [editingChannel]);

  const loadChannelData = async () => {
    if (!editingChannel) return;

    console.log('🔍 [CustomGPT] Loading channel data for editingChannel:', editingChannel);
    console.log('🔍 [CustomGPT] Channel ID:', editingChannel.id);
    console.log('🔍 [CustomGPT] CustomGPT Channel ID:', editingChannel.custom_gpt_channel_id);
    console.log('🔍 [CustomGPT] Account ID from editingChannel:', editingChannel.account_id);

    try {
      // Use account_id from editingChannel instead of localStorage
      const accountId = editingChannel.account_id;
      console.log('🔍 [CustomGPT] Using Account ID:', accountId);
      const response = await api.get(`/accounts/${accountId}/custom-gpt/channels/${editingChannel.id}`);
      console.log('✅ [CustomGPT] Loaded channel data:', response.data);
      const channelData = response.data;

      setFormData({
        name: channelData.name,
        gpt_name: channelData.gpt_name,
        gpt_description: channelData.gpt_description || '',
        greeting_enabled: channelData.greeting_enabled,
        greeting_message: channelData.greeting_message,
        auto_assignment_enabled: channelData.auto_assignment_enabled,
        conversation_retention_days: channelData.conversation_retention_days,
        rate_limit_per_minute: channelData.rate_limit_per_minute,
        rate_limit_per_hour: channelData.rate_limit_per_hour
      });

      setCreatedChannel(channelData);
    } catch (error) {
      console.error('Failed to load channel data:', error);
    }
  };

  const steps = [
    {
      label: 'Basic Information',
      description: 'Configure your Custom GPT channel'
    },
    {
      label: 'Channel Settings',
      description: 'Configure greeting and assignment'
    },
    {
      label: 'Rate Limits',
      description: 'Configure API rate limits'
    },
    {
      label: 'Integration Details',
      description: 'Get your API keys and URLs for OpenAI'
    }
  ];

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateStep = (step: number): boolean => {
    const newErrors: Record<string, string> = {};

    switch (step) {
      case 0: // Basic Information
        if (!formData.name.trim()) newErrors.name = 'Channel name is required';
        if (!formData.gpt_name.trim()) newErrors.gpt_name = 'GPT name is required';
        break;
      case 1: // Channel Settings
        if (formData.greeting_enabled && !formData.greeting_message.trim()) {
          newErrors.greeting_message = 'Greeting message is required when enabled';
        }
        break;
      case 2: // Rate Limits
        if (formData.rate_limit_per_minute < 1 || formData.rate_limit_per_minute > 1000) {
          newErrors.rate_limit_per_minute = 'Rate limit must be between 1 and 1000';
        }
        if (formData.rate_limit_per_hour < 1 || formData.rate_limit_per_hour > 100000) {
          newErrors.rate_limit_per_hour = 'Rate limit must be between 1 and 100000';
        }
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      if (activeStep === steps.length - 2) {
        // Last step before integration details - create/update channel
        handleSubmit();
      } else {
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
      }
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const accountId = parseInt(localStorage.getItem('account_id') || '1');

      if (editingChannel) {
        // Update existing channel
        const response = await api.put(
          `/accounts/${accountId}/custom-gpt/channels/${editingChannel.id}`,
          formData
        );
        setCreatedChannel(response.data);
        onChannelCreated(response.data);
      } else {
        // Create new channel
        const response = await api.post(
          `/accounts/${accountId}/custom-gpt/channels`,
          formData
        );
        setCreatedChannel(response.data);
        // Move to integration details step
        setActiveStep(steps.length - 1);
      }
    } catch (error: any) {
      console.error('Failed to save channel:', error);
      setErrors({ submit: error.response?.data?.detail || 'Failed to save channel' });
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    setCopySuccess(label);
    setTimeout(() => setCopySuccess(null), 2000);
  };

  const handleFinish = () => {
    if (createdChannel) {
      onChannelCreated(createdChannel);
    }
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0: // Basic Information
        return (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Configure the basic information for your Custom GPT channel. This will be used to
              identify the channel in Converse.ai.
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Channel Name"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  error={!!errors.name}
                  helperText={errors.name || 'Name for this channel in Converse.ai (e.g., "Customer Support GPT")'}
                  required
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="GPT Name"
                  value={formData.gpt_name}
                  onChange={(e) => handleInputChange('gpt_name', e.target.value)}
                  error={!!errors.gpt_name}
                  helperText={errors.gpt_name || 'Name of your Custom GPT in OpenAI'}
                  required
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="GPT Description"
                  value={formData.gpt_description}
                  onChange={(e) => handleInputChange('gpt_description', e.target.value)}
                  helperText="Description of what your Custom GPT does (optional)"
                />
              </Grid>
            </Grid>
          </Box>
        );

      case 1: // Channel Settings
        return (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Configure how the channel behaves when receiving messages from your Custom GPT.
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.greeting_enabled}
                      onChange={(e) => handleInputChange('greeting_enabled', e.target.checked)}
                    />
                  }
                  label="Enable Greeting Message"
                />
              </Grid>

              {formData.greeting_enabled && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Greeting Message"
                    value={formData.greeting_message}
                    onChange={(e) => handleInputChange('greeting_message', e.target.value)}
                    error={!!errors.greeting_message}
                    helperText={errors.greeting_message || 'Message sent when a new conversation starts'}
                  />
                </Grid>
              )}

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.auto_assignment_enabled}
                      onChange={(e) => handleInputChange('auto_assignment_enabled', e.target.checked)}
                    />
                  }
                  label="Enable Auto-Assignment"
                />
                <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 5 }}>
                  Automatically assign conversations to available agents
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  type="number"
                  label="Conversation Retention (Days)"
                  value={formData.conversation_retention_days}
                  onChange={(e) => handleInputChange('conversation_retention_days', parseInt(e.target.value))}
                  inputProps={{ min: 1, max: 365 }}
                  helperText="How long to retain conversation history (1-365 days)"
                />
              </Grid>
            </Grid>
          </Box>
        );

      case 2: // Rate Limits
        return (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Configure rate limits to prevent abuse and manage API usage.
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Requests Per Minute"
                  value={formData.rate_limit_per_minute}
                  onChange={(e) => handleInputChange('rate_limit_per_minute', parseInt(e.target.value))}
                  error={!!errors.rate_limit_per_minute}
                  helperText={errors.rate_limit_per_minute || 'Maximum requests per minute'}
                  inputProps={{ min: 1, max: 1000 }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Requests Per Hour"
                  value={formData.rate_limit_per_hour}
                  onChange={(e) => handleInputChange('rate_limit_per_hour', parseInt(e.target.value))}
                  error={!!errors.rate_limit_per_hour}
                  helperText={errors.rate_limit_per_hour || 'Maximum requests per hour'}
                  inputProps={{ min: 1, max: 100000 }}
                />
              </Grid>

              <Grid item xs={12}>
                <Alert severity="info" icon={<Info />}>
                  Rate limits help protect your system from excessive usage. Recommended defaults:
                  60 requests/minute, 1000 requests/hour.
                </Alert>
              </Grid>
            </Grid>
          </Box>
        );

      case 3: // Integration Details
        if (!createdChannel) {
          return (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CircularProgress />
              <Typography sx={{ mt: 2 }}>Creating channel...</Typography>
            </Box>
          );
        }

        return (
          <Box>
            <Alert severity="success" icon={<CheckCircle />} sx={{ mb: 3 }}>
              Custom GPT channel created successfully! Use the details below to configure your
              Custom GPT in OpenAI.
            </Alert>

            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AutoAwesome /> Integration Details
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Copy these three details to configure your Custom GPT as an Action in OpenAI:
            </Typography>

            <Grid container spacing={3}>
              {/* API Key */}
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="subtitle2" color="primary">
                        1. API Key
                      </Typography>
                      <Chip label="Required" size="small" color="error" />
                    </Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Use this API key for authentication in your Custom GPT Action configuration
                    </Typography>
                    <TextField
                      fullWidth
                      value={createdChannel.api_key}
                      type={showApiKey ? 'text' : 'password'}
                      InputProps={{
                        readOnly: true,
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton onClick={() => setShowApiKey(!showApiKey)} edge="end">
                              {showApiKey ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                            <IconButton
                              onClick={() => handleCopy(createdChannel.api_key, 'API Key')}
                              edge="end"
                            >
                              <ContentCopy />
                            </IconButton>
                          </InputAdornment>
                        )
                      }}
                      sx={{ mt: 1 }}
                    />
                  </CardContent>
                </Card>
              </Grid>

              {/* OpenAPI Schema URL */}
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="subtitle2" color="primary">
                        2. OpenAPI Schema URL
                      </Typography>
                      <Chip label="Required" size="small" color="error" />
                    </Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Import this schema URL in Custom GPT Actions to configure the API endpoints
                    </Typography>
                    <TextField
                      fullWidth
                      value={createdChannel.openapi_schema_url}
                      InputProps={{
                        readOnly: true,
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => window.open(createdChannel.openapi_schema_url, '_blank')}
                              edge="end"
                            >
                              <Launch />
                            </IconButton>
                            <IconButton
                              onClick={() => handleCopy(createdChannel.openapi_schema_url, 'OpenAPI Schema URL')}
                              edge="end"
                            >
                              <ContentCopy />
                            </IconButton>
                          </InputAdornment>
                        )
                      }}
                      sx={{ mt: 1 }}
                    />
                  </CardContent>
                </Card>
              </Grid>

              {/* Privacy Policy URL */}
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="subtitle2" color="primary">
                        3. Privacy Policy URL
                      </Typography>
                      <Chip label="Required" size="small" color="error" />
                    </Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Provide this privacy policy URL in your Custom GPT configuration
                    </Typography>
                    <TextField
                      fullWidth
                      value={createdChannel.privacy_policy_url}
                      InputProps={{
                        readOnly: true,
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => window.open(createdChannel.privacy_policy_url, '_blank')}
                              edge="end"
                            >
                              <Launch />
                            </IconButton>
                            <IconButton
                              onClick={() => handleCopy(createdChannel.privacy_policy_url, 'Privacy Policy URL')}
                              edge="end"
                            >
                              <ContentCopy />
                            </IconButton>
                          </InputAdornment>
                        )
                      }}
                      sx={{ mt: 1 }}
                    />
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography variant="subtitle2">Configuration Instructions</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography variant="body2" component="div">
                      <ol style={{ paddingLeft: '20px', margin: 0 }}>
                        <li style={{ marginBottom: '12px' }}>
                          Go to OpenAI and create or edit your Custom GPT
                        </li>
                        <li style={{ marginBottom: '12px' }}>
                          Navigate to the "Actions" section
                        </li>
                        <li style={{ marginBottom: '12px' }}>
                          Click "Import from URL" and paste the <strong>OpenAPI Schema URL</strong> above
                        </li>
                        <li style={{ marginBottom: '12px' }}>
                          Set Authentication to "API Key" and paste your <strong>API Key</strong> above
                        </li>
                        <li style={{ marginBottom: '12px' }}>
                          Add the <strong>Privacy Policy URL</strong> in your GPT settings
                        </li>
                        <li style={{ marginBottom: '12px' }}>
                          Test the integration and publish your Custom GPT
                        </li>
                      </ol>
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              </Grid>

              {/* Channel Statistics */}
              {createdChannel.total_requests > 0 && (
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" gutterBottom>
                    Channel Statistics
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={4}>
                      <Paper sx={{ p: 2, textAlign: 'center' }}>
                        <Typography variant="h4">{createdChannel.total_requests}</Typography>
                        <Typography variant="caption" color="text.secondary">Total Requests</Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={4}>
                      <Paper sx={{ p: 2, textAlign: 'center' }}>
                        <Typography variant="h4" color="success.main">{createdChannel.successful_requests}</Typography>
                        <Typography variant="caption" color="text.secondary">Successful</Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={4}>
                      <Paper sx={{ p: 2, textAlign: 'center' }}>
                        <Typography variant="h4" color="error.main">{createdChannel.failed_requests}</Typography>
                        <Typography variant="caption" color="text.secondary">Failed</Typography>
                      </Paper>
                    </Grid>
                  </Grid>
                </Grid>
              )}
            </Grid>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        maxHeight: '90vh',
        overflow: 'hidden',
        p: 3,
      }}
    >
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
        <SmartToy sx={{ fontSize: 40, color: 'primary.main' }} />
        <Box>
          <Typography variant="h5">
            {editingChannel ? 'Edit Custom GPT Channel' : 'Add Custom GPT Channel'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Integrate your OpenAI Custom GPT with Converse.ai for seamless customer support
          </Typography>
        </Box>
      </Box>

      {errors.submit && (
        <Alert severity="error" icon={<ErrorIcon />} sx={{ mb: 2, flexShrink: 0 }}>
          {errors.submit}
        </Alert>
      )}

      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', pr: 0.5 }}>
        <Stepper activeStep={activeStep} orientation="vertical">
          {steps.map((step, index) => (
            <Step key={step.label}>
              <StepLabel>
                <Typography variant="subtitle1">{step.label}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {step.description}
                </Typography>
              </StepLabel>
              <StepContent>
                {renderStepContent(index)}

                <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                  <Button
                    disabled={index === 0}
                    onClick={handleBack}
                    variant="outlined"
                  >
                    Back
                  </Button>

                  {index === steps.length - 1 ? (
                    <Button
                      variant="contained"
                      onClick={handleFinish}
                    >
                      Finish
                    </Button>
                  ) : (
                    <Button
                      variant="contained"
                      onClick={handleNext}
                      disabled={loading}
                    >
                      {loading ? <CircularProgress size={24} /> : index === steps.length - 2 ? 'Create Channel' : 'Next'}
                    </Button>
                  )}

                  <Button onClick={onCancel} disabled={loading}>
                    Cancel
                  </Button>
                </Box>
              </StepContent>
            </Step>
          ))}
        </Stepper>
      </Box>

      <Snackbar
        open={!!copySuccess}
        autoHideDuration={2000}
        onClose={() => setCopySuccess(null)}
        message={`${copySuccess} copied to clipboard!`}
      />
    </Box>
  );
};

export default CustomGPTChannelConfig;
